import React from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { SecretInput } from "../SecretInput"
import { HTTP_METHOD_OPTIONS } from "../websocket-config/types"
import { JsonField } from "./JsonField"
import { CHAT_WEBSOCKET_TOOLTIPS } from "./tooltips"
import type { ChatWebSocketFieldProps } from "./types"

/**
 * HTTP Setup fields for Chat WebSocket HTTP-first connection mode.
 * Renders: http_url, http_method, http_request_body, http_headers, websocket_url_response_path
 */
export const ChatWebSocketHttpFirstSetupFields: React.FC<
  ChatWebSocketFieldProps
> = ({ metadata, onMetadataUpdate, disabled = false }) => {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">HTTP Setup</h4>

      {/* HTTP Endpoint URL */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="chat-ws-http-url"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.httpUrl}
        >
          HTTP Endpoint URL
        </FieldLabel>
        <Input
          id="chat-ws-http-url"
          aria-label="HTTP Endpoint URL"
          value={metadata.http_url || ""}
          placeholder="Enter your HTTP setup endpoint"
          onChange={(e) => onMetadataUpdate({ http_url: e.target.value })}
          disabled={disabled}
        />
      </div>

      {/* HTTP Method */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="chat-ws-http-method"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.httpMethod}
        >
          HTTP Method
        </FieldLabel>
        <Select
          value={metadata.http_method || "POST"}
          onValueChange={(value) => onMetadataUpdate({ http_method: value })}
          disabled={disabled}
        >
          <SelectTrigger id="chat-ws-http-method" aria-label="HTTP Method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HTTP_METHOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Request Body (JSON) */}
      <JsonField
        id="chat-ws-http-body"
        label="Request Body"
        ariaLabel="HTTP Request Body"
        placeholder={'{\n  "channel": "web",\n  "language": "en"\n}'}
        description="JSON body sent with the HTTP request to create the session."
        value={metadata.http_request_body}
        fieldName="http_request_body"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={4}
        tooltip={CHAT_WEBSOCKET_TOOLTIPS.httpRequestBody}
      />

      {/* HTTP Headers (JSON) */}
      <JsonField
        id="chat-ws-http-headers"
        label="HTTP Headers"
        ariaLabel="HTTP Headers"
        placeholder={
          '{\n  "Authorization": "Bearer YOUR_API_KEY",\n  "Content-Type": "application/json"\n}'
        }
        description="Headers for the HTTP setup request. Separate from the WebSocket connection headers."
        value={metadata.http_headers}
        fieldName="http_headers"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={4}
        tooltip={CHAT_WEBSOCKET_TOOLTIPS.httpHeaders}
      />

      {/* WebSocket URL Response Path */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="chat-ws-response-path"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.websocketUrlResponsePath}
        >
          WebSocket URL Path in Response
        </FieldLabel>
        <Input
          id="chat-ws-response-path"
          aria-label="WebSocket URL Path in Response"
          value={metadata.websocket_url_response_path || ""}
          placeholder="Enter the WebSocket URL response path"
          onChange={(e) =>
            onMetadataUpdate({
              websocket_url_response_path: e.target.value,
            })
          }
          disabled={disabled}
        />
      </div>
    </div>
  )
}

/**
 * WebSocket connection fields shown in HTTP-first mode.
 * Renders: authorization_header, initialization_json (optional), custom_headers
 */
export const ChatWebSocketHttpFirstConnectionFields: React.FC<
  ChatWebSocketFieldProps
> = ({ metadata, onMetadataUpdate, disabled = false }) => {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">
        WebSocket Connection
      </h4>

      {/* Authorization Header (Optional) */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="chat-ws-httpfirst-auth"
          tooltip={CHAT_WEBSOCKET_TOOLTIPS.authorizationHeader}
        >
          Authorization Header
        </FieldLabel>
        <SecretInput
          id="chat-ws-httpfirst-auth"
          aria-label="Authorization Header"
          revealLabel="authorization header"
          value={metadata.authorization_header || ""}
          placeholder="Enter an authorization header"
          onChange={(e) =>
            onMetadataUpdate({ authorization_header: e.target.value })
          }
          disabled={disabled}
        />
      </div>

      {/* Initialization JSON (Optional) */}
      <JsonField
        id="chat-ws-httpfirst-init"
        label="Initialization JSON"
        ariaLabel="Initialization JSON"
        placeholder={'{\n  "action": "subscribe",\n  "topics": ["messages"]\n}'}
        description="Optional JSON payload sent after the WebSocket connection is established. No message is sent if left empty (recommended when the session was already configured via HTTP)."
        value={metadata.initialization_json}
        fieldName="initialization_json"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        tooltip={`${CHAT_WEBSOCKET_TOOLTIPS.initializationJson} Leaving this empty is recommended when HTTP setup already configured the session.`}
      />

      {/* Custom Headers (Optional) */}
      <JsonField
        id="chat-ws-httpfirst-headers"
        label="Custom Headers"
        ariaLabel="Custom Headers"
        placeholder={'{\n  "X-Coval-Simulation-Id": "{{simulation_id}}"\n}'}
        description="Additional headers to include in the WebSocket connection request."
        value={metadata.custom_headers}
        fieldName="custom_headers"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={3}
        tooltip={CHAT_WEBSOCKET_TOOLTIPS.customHeaders}
      />
    </div>
  )
}
